import type { Metric } from "web-vitals";
import { getSeoRoute, SITE_URL } from "../seo";

const GA_MEASUREMENT_ID = "G-D2WPNYSKBJ";

type ContactChannel = "whatsapp" | "email" | "phone";
type ContactPlacement = "hero" | "contact_band" | "footer" | "service_cta";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function trackContactClick(channel: ContactChannel, placement: ContactPlacement) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const route = getSeoRoute(window.location.pathname);
  if (!route?.index) return;

  // Um clique não comprova um lead. Não envia destino, query string ou referência pessoal.
  try {
    window.gtag("event", "contact_click", {
      contact_channel: channel,
      contact_placement: placement,
      page_path: route.path,
      page_location: `${SITE_URL}${route.path}`,
      page_referrer: "",
    });
  } catch {
    // Falha do serviço analítico nunca impede o contato.
  }
}

function sendToAnalytics({ name, delta, value, id, rating }: Metric) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", name, {
      event_category: "Web Vitals",
      // Google Analytics recomenda multiplicar CLS por 1000 para arredondamento inteiro
      value: Math.round(name === "CLS" ? delta * 1000 : delta),
      event_label: id,
      non_interaction: true,
      metric_id: id,
      metric_value: value,
      metric_delta: delta,
      metric_rating: rating,
    });
  }
}

export function initAnalytics() {
  if (typeof window === "undefined") return;
  const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  if (!isLocal && !document.getElementById("gtag-script")) {
    const script = document.createElement("script");
    script.id = "gtag-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
      send_page_view: true,
    });
  }

  // Monitoramento de Core Web Vitals no cliente real (RUM)
  initWebVitals();
}

export async function initWebVitals() {
  if (typeof window === "undefined") return;
  try {
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import("web-vitals");
    onCLS(sendToAnalytics);
    onINP(sendToAnalytics);
    onLCP(sendToAnalytics);
    onFCP(sendToAnalytics);
    onTTFB(sendToAnalytics);
  } catch {
    // Falha silenciosa em navegadores ou contextos sem suporte a PerformanceObserver
  }
}
